import { getNotifications, deleteNotification, createOrUpdateNotif } from "@/api/notification/notification";
import moment from "moment";
import { Message } from 'view-design';

const pageSize = 5;

export default {
    namespaced: true,
    state: {
        searchParams: {
            type: '',
            status: '',
            term: ''
        },

        notifications: [],
        total: -1,

        loading: false,
        lastFetchedTime: null,
        lastNoDataTime: null,
        
        detailLoading: false,
        notifDetail: null,
        notifDetailAction: '',

        deleteLoading: false,
        toBeDeleted: null,
        deleteConfirmVisible: false,
    },
    mutations: {
        // reset
        resetNotifs(state) {
            state.notifications = [];
            state.total = -1;
            state.lastFetchedTime = null;
            state.lastNoDataTime = null;
        },
        resetSearchParams(state) {
            state.searchParams = {
                type: '',
                status: '',
                term: ''
            }
        },

        // get
        setNotifs(state, payload) {
            state.lastFetchedTime = moment();
            state.total = payload.total;
            Array.prototype.push.apply(state.notifications, payload.notifications);
            state.notifications.sort((a, b) => a.timestamp - b.timestamp);
            if (state.notifications.length >= payload.total) state.lastNoDataTime = new Date();
            else state.lastNoDataTime = null;
        },

        // filter
        updateSearchParams(state, payload) {
            const newParams = {
                ...state.searchParams
            };
            newParams[payload.name] = payload.val;
            state.searchParams = newParams;
        },

        // action state control
        resetNotifDetail(state) {
            state.notifDetail = null;
            state.notifDetailAction = '';
        },
        setNotifDetail(state, payload) {
            state.notifDetail = payload.notification;
            state.notifDetailAction = payload.action;
        },

        // delete
        resetToBeDeleted(state) {
            state.toBeDeleted = null;
            state.deleteConfirmVisible = false;
        },
        setToBeDeleted(state, payload) {
            state.toBeDeleted = payload.notification;
            state.deleteConfirmVisible = true;
        },
        deleteSuccess(state) {
            if (state.notifDetail === state.toBeDeleted) {
                state.notifDetail = null;
                state.notifDetailAction = '';
            }

            state.toBeDeleted = null;
            state.deleteConfirmVisible = false;
        },

        // loading state
        setLoading(state) {
            state.loading = true;
        },
        resetLoading(state) {
            state.loading = false;
        },

        setDetailLoading(state) {
            state.detailLoading = true;
        },
        resetDetailLoading(state) {
            state.detailLoading = false;
        },

        setDeleteLoading(state) {
            state.deleteLoading = true;
        },
        resetDeleteLoading(state) {
            state.deleteLoading = false;
        },
    },
    actions: {
        // reset
        reset({ commit }) {
            commit("resetNotifs");
            commit("resetSearchParams");
            commit("resetNotifDetail");
            commit("resetToBeDeleted");
        },

        // get
        async getNotifs({ commit, state }) {
            if (state.loading) return;
            if (state.total >= 0 && state.notifications.length >= state.total) {
                if (new Date() - state.lastNoDataTime < 1000 * 60 * 5) return;
                else commit("resetNotifs");
            }
            if (state.lastFetchedTime && moment().date() !== state.lastFetchedTime.date()) commit("resetNotifs");

            // 后端排序按照时间轴方式
            try {
                commit("setLoading");
                const res = await getNotifications({
                    ...formatNotifFilter(state.searchParams),
                    pageNumber: parseInt(state.notifications.length / pageSize) + 1,
                    pageSize,
                });
                commit("setNotifs", res);
            } catch (e) {
                console.error(e);
                // Message.error('出错啦');
            } finally {
                commit("resetLoading");
            }
        },

        // filter
        updateSearchParams({ commit, dispatch}, payload) {
            commit("updateSearchParams", payload);
            commit("resetNotifs");
            dispatch('getNotifs');
        },

        // action state control
        resetNotifDetail({ commit }) {
            commit("resetNotifDetail");
        },
        setToView({ commit }, payload) {
            payload.action = 'view';
            commit("setNotifDetail", payload);
        },
        setToModify({ commit }, payload) {
            payload.action = 'modify';
            commit("setNotifDetail", payload);
        },
        setToCreate({ commit }) {
            commit("setNotifDetail", { notification: null, action: 'new' });
        },

        // create & update
        async createOrUpdateNotif({ commit, state, dispatch }, payload) {
            const formData = formatNotifFormData(payload.formData);
            if (state.notifDetail) formData.id = state.notifDetail.id;
            try {
                commit("setDetailLoading");
                const res = await createOrUpdateNotif(formData);
                if (res) {
                    commit("resetNotifs");
                    dispatch('getNotifs');
                    Message.success('操作成功');
                    commit("resetNotifDetail");
                } else {
                    throw res;
                }
            } catch (e) {
                console.error(e);
                // Message.error('操作失败');
            } finally {
                commit("resetDetailLoading");
            }
        },

        // Delete
        resetToBeDeleted({ commit }) {
            commit("resetToBeDeleted");
        },
        setToBeDeleted({ commit }, payload) {
            commit("setToBeDeleted", payload);
        },
        async confirmDelete({ commit, state, dispatch }) {
            if (state.toBeDeleted) {
                try {
                    commit("setDeleteLoading");
                    const res = await deleteNotification(state.toBeDeleted.id);
                    if (res) {
                        commit("deleteSuccess");
                        commit("resetNotifs");
                        dispatch('getNotifs');
                        Message.success('删除成功');
                    } else {
                        throw res;
                    }
                } catch (e) {
                    console.error(e);
                    // Message.error('出错啦');
                } finally {
                    commit("resetDeleteLoading");
                }
            } else {
                Message.error('消息不存在');
            }
        },        
    },
};

function formatNotifFilter(params) {
    const ret = {};
    if (params) {
        if (params.type && params.type !== 'all')
            ret.type = params.type;
        if (params.status && params.status !== 'all')
            ret.status = params.status;
        if (params.term)
            ret.term = params.term;
    }
    return ret;
}

function formatNotifFormData(formData) {
    const ret = {
        type: formData.msgType,
        subject: formData.subject,
        content: formData.content,
        viaWeb: formData.via.web,
        viaPhone: formData.via.phone,
        viaEmail: formData.via.email,
        indexNotif: formData.via.indexNotif,
        receiversCategory: [],
        receiversUsers: [],
    };

    formData.receiverFilters.forEach(f => {
        if (f.type === 'category')
            ret.receiversCategory.push(f.key);
        else if (f.type === 'user')
            ret.receiversUsers.push(f.key);
    });

    ret.timestamp = formData.date;

    if (formData.time instanceof Date) {
        ret.timestamp.setHours(formData.time.getHours());
        ret.timestamp.setMinutes(formData.time.getMinutes());
        ret.timestamp.setSeconds(formData.time.getSeconds());
    } else {
        const hms = formData.time.split(':');
        ret.timestamp.setHours(parseInt(hms[0]));
        ret.timestamp.setMinutes(parseInt(hms[1]));
        ret.timestamp.setSeconds(parseInt(hms[2]));
    }
    return ret;
}